/**
 * One World's live socket: ticket, WebSocket, hello, then events and
 * presence, for as long as the World is in view.
 *
 * The key never goes in a URL. Every attempt asks for a fresh single-use
 * ticket (`POST …/watch-tickets` with the viewer's name and colour) and
 * opens `…/watch?ticket=…&presence=true`. The link is live only once the
 * World says hello. When the socket drops it retries with full-jitter
 * backoff (500 ms doubling to a 15 s cap), forever while the tab is visible,
 * and meanwhile reads `getWorld` every 5 s so a change made while it was
 * down is heard as a new head. Missed events cost a journal read, never
 * correctness, so polling is enough to bridge the gap.
 */

import type { Args, Client, Json, Link, LinkState, PresenceIn, PresenceOut, Profile, Unsub, WorldDescribe, WorldEvent } from '../contracts';

import { decodePresenceOut, PresenceGate, type Timers } from './presence';
import { worldRef } from '../worlds';
import { isObj } from '../model/json';

export const RETRY_BASE_MS = 500;
export const RETRY_CAP_MS = 15_000;
export const POLL_MS = 5_000;
export const HELLO_MS = 10_000;
export const PING_MS = 30_000;
/** After this many failed attempts in a row the wire shows polling, not a countdown. */
export const POLLING_AFTER = 3;

export interface LinkDeps {
  readonly apiBase: string;
  readonly client: Client;
  readonly profile: () => Profile;
  readonly socket: (url: string) => WebSocket;
  readonly now: () => number;
  readonly timers: Timers;
  readonly random: () => number;
  /** Whether the tab is hidden when the link opens. */
  readonly hidden?: () => boolean;
}

/** A Link, plus what the pool needs to drive it. */
export interface LiveLink extends Link {
  readonly presence: boolean;
  /** Try now: the browser came back online, or the tab came back into view. */
  retryNow(): void;
  /** A hidden tab keeps its socket but sends no presence and does not re-upgrade. */
  setHidden(hidden: boolean): void;
}

/** Full jitter: anywhere from 0 to the doubled wait, capped. `attempt` counts from 0. */
export const backoffMs = (attempt: number, random: () => number, base = RETRY_BASE_MS, cap = RETRY_CAP_MS): number =>
  Math.floor(random() * Math.min(cap, base * 2 ** Math.max(0, attempt)));

/** Where a browser opens the watch: the ticket in the query, since a WebSocket carries no header. A persona's key names its World. */
export const watchUrl = (apiBase: string, world: string, ticket: string, presence: boolean): string =>
  `${apiBase.replace(/^http/, 'ws')}/v1/worlds/${encodeURIComponent(worldRef(world).w)}/watch?ticket=${encodeURIComponent(ticket)}${presence ? '&presence=true' : ''}`;

/** A World's describe, from a hello or a getWorld answer (which has these fields and more). */
export const describeOf = (v: Json | undefined): WorldDescribe | null => {
  if (!isObj(v)) return null;
  const { globalName, preset, leafSlotBits, allowDisplace, tieBreak, seats, occupiers, relays, seq, head } = v;
  if (typeof globalName !== 'string' || typeof preset !== 'string' || typeof leafSlotBits !== 'number' || typeof allowDisplace !== 'boolean'
    || typeof tieBreak !== 'string' || typeof seats !== 'number' || typeof occupiers !== 'number' || typeof relays !== 'number'
    || typeof seq !== 'number' || typeof head !== 'string') return null;
  return { globalName, preset, leafSlotBits, allowDisplace, tieBreak, seats, occupiers, relays, seq, head };
};

/** A WorldEvent, if the frame is one. */
export const eventOf = (v: Json): WorldEvent | null => {
  if (!isObj(v)) return null;
  const { id, type, world, at, commitSeq, head, data } = v;
  if (typeof id !== 'string' || typeof type !== 'string' || typeof world !== 'string' || typeof at !== 'number') return null;
  if (commitSeq !== null && typeof commitSeq !== 'number') return null;
  if (head !== null && typeof head !== 'string') return null;
  return { id, type, world, at, commitSeq, head, data: isObj(data) ? data : {} };
};

export function createLink(world: string, opts: { presence: boolean }, deps: LinkDeps): LiveLink {
  let state: LinkState = 'connecting';
  let closed = false;
  let hidden = deps.hidden?.() ?? false;
  let socket: WebSocket | null = null;
  let gate: PresenceGate | null = null;
  let full = false;
  let failures = 0;
  let generation = 0;
  /** Tickets are not offered here: poll, and try a socket now and then. */
  let pollOnly = false;
  let head: string | null = null;
  let nextAt: number | null = null;
  let retryTimer: unknown = null, pollTimer: unknown = null, helloTimer: unknown = null, pingTimer: unknown = null;

  const hellos = new Set<(d: WorldDescribe) => void>();
  const events = new Set<(e: WorldEvent) => void>();
  const presences = new Set<(f: PresenceOut) => void>();
  const states = new Set<(s: LinkState, why?: string) => void>();
  const each = <T>(set: Set<(x: T) => void>, x: T) => { for (const fn of [...set]) { try { fn(x); } catch { /* the listener's own fault */ } } };
  const sub = <T>(set: Set<T>, fn: T): Unsub => { set.add(fn); return () => { set.delete(fn); }; };

  const setState = (s: LinkState, why?: string) => {
    state = s;
    for (const fn of [...states]) { try { fn(s, why); } catch { /* the listener's own fault */ } }
  };
  const clear = (id: unknown) => { if (id !== null) deps.timers.clear(id); };
  const detach = (s: WebSocket) => { s.onmessage = null; s.onclose = null; s.onerror = null; s.onopen = null; };

  const stopSocket = () => {
    clear(helloTimer); helloTimer = null;
    clear(pingTimer); pingTimer = null;
    gate?.close(); gate = null;
    full = false;
    const s = socket;
    socket = null;
    if (s) {
      detach(s);
      try { s.close(1000, 'done'); } catch { /* already closing */ }
    }
  };

  const finish = (why: string) => {
    closed = true;
    generation++;
    for (const [stop, timer] of inflight) { deps.timers.clear(timer); stop.abort(); }
    inflight.clear();
    clear(retryTimer); retryTimer = null;
    clear(pollTimer); pollTimer = null;
    nextAt = null;
    stopSocket();
    setState('closed', why);
  };

  /** Read through a call, since the state may change while a call is out. */
  const isLive = () => state === 'live';

  /** Calls out now, each with the timer that gives it up. */
  const inflight = new Map<AbortController, unknown>();
  /** A call the link makes, given up after the hello budget so a hung request cannot stall the link. */
  const timed = async (op: string, args: Args) => {
    const stop = new AbortController();
    inflight.set(stop, deps.timers.set(() => stop.abort(), HELLO_MS));
    try {
      return await deps.client.call(op, args, { source: { kind: 'auto' }, signal: stop.signal });
    } finally {
      if (inflight.has(stop)) deps.timers.clear(inflight.get(stop));
      inflight.delete(stop);
    }
  };

  const poll = async () => {
    pollTimer = null;
    if (closed || isLive()) return;
    const record = await timed('getWorld', { w: world });
    if (closed || isLive()) return;
    const status = record.answer?.status ?? 0;
    if (status === 401 || status === 404) {
      finish(detailOf(record.answer?.body) ?? `the World answered ${status}`);
      return;
    }
    const d = status === 200 ? describeOf(record.answer?.body) : null;
    // A head that moved while the socket was down: the same news a hello brings.
    if (d && d.head !== head) {
      head = d.head;
      each(hellos, d);
    }
    if (pollTimer === null) pollTimer = deps.timers.set(() => { void poll(); }, POLL_MS);
  };

  const startPolling = () => {
    if (pollTimer === null && !closed) pollTimer = deps.timers.set(() => { void poll(); }, POLL_MS);
  };

  const fail = (why: string) => {
    if (closed) return;
    failures++;
    startPolling();
    const label: LinkState = pollOnly || failures >= POLLING_AFTER ? 'polling' : 'retrying';
    clear(retryTimer); retryTimer = null;
    if (hidden) {
      nextAt = null;
      setState(label, `${why}; paused while the tab is hidden`);
      return;
    }
    const delay = pollOnly ? RETRY_CAP_MS : backoffMs(failures - 1, deps.random);
    nextAt = deps.now() + delay;
    retryTimer = deps.timers.set(() => {
      retryTimer = null;
      nextAt = null;
      if (hidden) { setState(state, 'paused while the tab is hidden'); return; }
      void connect();
    }, delay);
    setState(label, `${why}; next try in ${(delay / 1000).toFixed(1)} s`);
  };

  const armPing = () => {
    pingTimer = deps.timers.set(() => {
      pingTimer = null;
      if (state !== 'live' || !socket) return;
      try { socket.send('ping'); } catch { /* its close will follow */ }
      armPing();
    }, PING_MS);
  };

  const onMessage = (data: unknown) => {
    if (typeof data !== 'string' || data === 'pong') return;
    let message: Json;
    try { message = JSON.parse(data) as Json; } catch { return; }
    if (!isObj(message) || typeof message.type !== 'string') return;
    if (message.type === 'hello') {
      clear(helloTimer); helloTimer = null;
      clear(pollTimer); pollTimer = null;
      failures = 0;
      pollOnly = false;
      nextAt = null;
      const d = describeOf(message.world);
      if (d) head = d.head;
      if (opts.presence && !full) {
        gate = new PresenceGate({
          now: deps.now, timers: deps.timers,
          write: (text) => { if (socket && socket.readyState === 1) socket.send(text); },
        });
        if (hidden) gate.pause();
      }
      clear(pingTimer);
      armPing();
      if (d) each(hellos, d);
      setState('live');
      return;
    }
    if (message.type.startsWith('presence')) {
      const f = decodePresenceOut(message);
      if (!f) return;
      if (f.type === 'presence.full') { full = true; gate?.close(); gate = null; }
      each(presences, f);
      return;
    }
    const e = eventOf(message);
    if (!e) return;
    if (e.head) head = e.head;
    each(events, e);
  };

  const open = (ticket: string) => {
    let s: WebSocket;
    try {
      s = deps.socket(watchUrl(deps.apiBase, world, ticket, opts.presence));
    } catch (error) {
      fail(`the watch could not be opened (${String(error)})`);
      return;
    }
    socket = s;
    s.onmessage = (m: MessageEvent) => { if (socket === s) onMessage(m.data); };
    s.onclose = (e: CloseEvent) => {
      if (socket !== s) return;
      const wasLive = state === 'live';
      stopSocket();
      const code = e?.code ? ` (${e.code}${e.reason ? ` ${e.reason}` : ''})` : '';
      fail(wasLive ? `the watch closed${code}` : `the watch did not open${code}`);
    };
    // An error is always followed by a close, which does the work.
    s.onerror = () => undefined;
    helloTimer = deps.timers.set(() => {
      helloTimer = null;
      if (socket !== s || state === 'live') return;
      stopSocket();
      fail('the World did not say hello');
    }, HELLO_MS);
  };

  const connect = async () => {
    if (closed || state === 'live') return;
    clear(retryTimer); retryTimer = null;
    nextAt = null;
    const gen = ++generation;
    setState('connecting');
    const p = deps.profile();
    const record = await timed('watchTicket', { w: world, body: { name: p.name, color: p.color } });
    if (closed || gen !== generation) return;
    const status = record.answer?.status ?? 0;
    const body = record.answer?.body;
    const ticket = status >= 200 && status < 300 && isObj(body) && typeof body.ticket === 'string' ? body.ticket : null;
    if (ticket) { open(ticket); return; }
    const detail = detailOf(body) ?? `the watch ticket answered ${status}`;
    if (status === 401 || status === 403 || status === 404) { finish(detail); return; }
    if (status === 501) pollOnly = true;
    fail(detail);
  };

  queueMicrotask(() => { void connect(); });

  return {
    world,
    presence: opts.presence,
    state: () => state,
    presenceFull: () => full,
    nextRetryAt: () => nextAt,
    onHello: (fn) => sub(hellos, fn),
    onEvent: (fn) => sub(events, fn),
    onPresence: (fn) => sub(presences, fn),
    onState: (fn) => sub(states, fn),
    send(frame: PresenceIn): boolean {
      if (!opts.presence || full || state !== 'live' || !gate) return false;
      return gate.offer(frame);
    },
    retryNow() {
      if (closed || state === 'live' || state === 'connecting') return;
      void connect();
    },
    setHidden(h: boolean) {
      hidden = h;
      if (h) gate?.pause();
      else {
        gate?.resume();
        if (!closed && state !== 'live' && state !== 'connecting') void connect();
      }
    },
    close() {
      if (closed) return;
      finish('closed');
    },
  };
}

const detailOf = (body: Json | undefined): string | null => (isObj(body) && typeof body.detail === 'string' ? body.detail : null);
